import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { ApplicationForUser } from '../../../models/borrower-portal/application-for-user-model';
import { PortalServiceFactory } from '../../../services/portal-service.factory';
import { DirectUploadService, DirectUploadProgress } from '../../../services/direct-upload.service';
import { PortalBaseComponent } from '../../portal-base.component';
import { firstValueFrom } from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { NotificationService } from 'projects/shared/services/notification.service';
import { getErrorMessageOrDefault } from 'projects/shared/utils/error.utils';
import { LoanDocTask } from '../../../models/tasks/loan-doc-task.model';
import { FileUploadModel } from "../../../models/tasks/file-upload.model";
import { OtherBorrowersOnLoanModel } from '../../../models/borrower-portal/other-borrowers-on-loan-model';

/** A borrower the uploaded documents can be attached to, as shown in the picker. */
interface BorrowerChoice {
  borrowerId: number;
  borrowerName: string;
}

@Component({
    selector: 'app-add-documents-to-loan-dialog',
    templateUrl: './add-documents-to-loan-dialog.component.html',
    standalone: false
})
export class AddDocumentsToLoanDialogComponent extends PortalBaseComponent implements OnInit {

  @Input()
  application: ApplicationForUser = new ApplicationForUser();

  @Input()
  otherBorrowersOnLoan: OtherBorrowersOnLoanModel[] = [];


  protected notes: string = '';
  protected selectedBorrowerId: number;

  // Borrowers the uploaded docs can be attached to. Borrower portal: the signed-in borrower plus
  // any portal-merged co-borrowers. Agent portal (no myDetails): all borrowers on the loan. The
  // picker is only shown when there is more than one choice (WI 24806).
  protected borrowerChoices: BorrowerChoice[] = [];

  protected isUploading: boolean = false;

  private readonly _destroyRef = inject(DestroyRef);
  private _completedUploads = 0;
  private _totalUploads = 0;

  constructor(public activeModal: NgbActiveModal, private readonly portalServiceFactory: PortalServiceFactory,
    private readonly _notifsService: NotificationService,
    private readonly _directUploadService: DirectUploadService
  ) {
    super(portalServiceFactory);
  }

  ngOnInit() {
    this.borrowerChoices = this.buildBorrowerChoices();

    // Default selection:
    // - Borrower portal: the signed-in borrower (myDetails).
    // - Agent portal with a single borrower on the loan: that borrower (the primary).
    // - Agent portal with co-borrowers: default to the primary borrower; the agent can change it.
    this.selectedBorrowerId =
      this.application.myDetails?.borrowerId
      ?? this.application.primaryBorrowerId
      ?? this.borrowerChoices[0]?.borrowerId;
  }

  private buildBorrowerChoices(): BorrowerChoice[] {
    const myBorrowerId = this.application.myDetails?.borrowerId;
    if (myBorrowerId) {
      // Borrower portal: the signed-in borrower plus the portal-merged co-borrowers passed in.
      return [
        { borrowerId: myBorrowerId, borrowerName: this.application.myDetails.borrowerName ?? '' },
        ...this.toBorrowerChoices(this.otherBorrowersOnLoan),
      ];
    }
    // Agent portal: every borrower on the loan (the list already includes the primary).
    return this.toBorrowerChoices(this.application.otherBorrowersOnLoan);
  }

  private toBorrowerChoices(borrowers: OtherBorrowersOnLoanModel[] | undefined): BorrowerChoice[] {
    return (borrowers ?? [])
      .filter(borrower => borrower.borrowerId != null)
      .map(borrower => ({ borrowerId: borrower.borrowerId!, borrowerName: borrower.borrowerName ?? '' }));
  }

  protected onUploadClicked = async (files: FileUploadModel[]) => {
    if (this.selectedBorrowerId == null) {
      this._notifsService.showError('Please select a borrower to attach the documents to.', 'Error!');
      return;
    }

    // Own the uploading state before the async task lookup so the button is disabled during it
    // (prevents duplicate-task double-clicks) and, crucially, so any early return below drives
    // isUploading back to false and re-enables the button. (LLS-138)
    this.isUploading = true;

    let taskToAttachFilesTo: LoanDocTask = null;
    // First get the task using the portal service
    try {
      taskToAttachFilesTo = await firstValueFrom(this.portalService.getTaskForFileUpload(this.application.applicationId,
        this.selectedBorrowerId, this.notes));
    } catch (err) {
      const errorMessage = getErrorMessageOrDefault(err, {
        defaultMessage: 'Error getting task to attach files to.',
      });
      this._notifsService.showError(_.escape(errorMessage), 'Error!');
      this.isUploading = false;
      return;
    }

    if (!taskToAttachFilesTo?.loanDocTaskId) {
      this._notifsService.showError('Error getting task to attach files to.', 'Error!');
      this.isUploading = false;
      return;
    }

    // Then upload the files
    this._totalUploads = files.length;
    this._completedUploads = 0;

    files.forEach((file, index) => {
      this._directUploadService.uploadToTask(
        this.application.applicationId,
        [file.file],
        taskToAttachFilesTo.loanDocTaskId,
        this.notes?.trim() || '',
        (progress: DirectUploadProgress) => {
          file.progress = progress.percent;
        },
        file.password
      )
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (results) => {
            const result = results[0];
            if (result && !result.success) {
              // The toast renders HTML; the server's message can echo the file name
              this._notifsService.showError(_.escape(result.error || 'Upload failed'), 'Error!');
              file.progress = 0;
            }
            this._completedUploads++;
            this.checkAllUploadsComplete();
          },
          error: (err: any) => {
            let errorMessage;
            try {
              // Try to parse the error if it's a string
              errorMessage = typeof err.error === 'string' ? JSON.parse(err.error).detail : err?.error?.detail;
            } catch (e) {
              // If parsing fails, use the original error
              errorMessage = getErrorMessageOrDefault(err, {
                defaultMessage: 'Error uploading additional documents to loan.',
              });
            }

            this._notifsService.showError(_.escape(errorMessage), 'Error!');
            file.progress = 0;
            this._completedUploads++;
            this.checkAllUploadsComplete();
          }
        });
    });
  }

  private checkAllUploadsComplete(): void {
    if (this._completedUploads >= this._totalUploads) {
      this.isUploading = false;
      this.activeModal.close();
    }
  }
}
